package com.schoolconnect.app.feature.parentnotes

import com.schoolconnect.app.datasource.network.response.ParentNoteReplyResponse
import com.schoolconnect.app.datasource.network.response.ParentNoteResponse
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ParentChildSelectionStore
import com.schoolconnect.app.feature.common.PageRequest
import com.schoolconnect.app.feature.common.pageCountForTotal
import com.schoolconnect.app.repository.parentportalnotes.ParentPortalNotesRepository
import com.schoolconnect.app.repository.profile.ProfileRepository
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.json.*
import kotlin.time.Clock

class ParentPortalNotesViewModel(initialChildren: List<ParentAttendanceChild> = emptyList()) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val _state = MutableStateFlow(ParentPortalNotesState(children = initialChildren))
    val state: StateFlow<ParentPortalNotesState> = _state.asStateFlow()
    private var listJob: Job? = null
    private var summaryJob: Job? = null
    private var listRequestId = 0L
    init {
        observeGlobalSelection()
        load()
        _state.value.selectedChild?.id?.let(::loadSummary)
    }

    private fun observeGlobalSelection() {
        scope.launch {
            ParentChildSelectionStore.selectedChildId.collect { childId ->
                val index = _state.value.children.indexOfFirst { it.id == childId }
                if (index >= 0 && index != _state.value.selectedChildIndex) onEvent(ParentPortalNotesEvent.ChildSelected(index))
            }
        }
    }

    fun onEvent(event: ParentPortalNotesEvent) {
        when (event) {
            is ParentPortalNotesEvent.ChildSelected -> {
                _state.update { it.copy(selectedChildIndex = event.index.coerceIn(0, it.children.lastIndex.coerceAtLeast(0)), teacherId = "", teacherName = "All Teachers", teacherOptions = emptyList(), pageIndex = 0, matchingCount = 0) }
                load()
                _state.value.selectedChild?.id?.let(::loadSummary)
            }
            is ParentPortalNotesEvent.SearchChanged -> { _state.update { it.copy(search = event.value, pageIndex = 0) }; load(debounce = true) }
            is ParentPortalNotesEvent.TeacherChanged -> { _state.update { it.copy(teacherId = event.option?.id.orEmpty(), teacherName = event.option?.name ?: "All Teachers", pageIndex = 0) }; load() }
            is ParentPortalNotesEvent.StatusChanged -> { _state.update { it.copy(status = event.value, pageIndex = 0) }; load() }
            is ParentPortalNotesEvent.PeriodChanged -> {
                if (event.value == "Custom Range") return
                _state.update { it.copy(period = event.value, periodFrom = "", periodTo = "", pageIndex = 0) }
                load()
            }
            is ParentPortalNotesEvent.CustomPeriodApplied -> { _state.update { it.copy(period = "Custom Range", periodFrom = event.from, periodTo = event.to, pageIndex = 0) }; load() }
            ParentPortalNotesEvent.FiltersCleared -> {
                _state.update { it.copy(search = "", status = "All", period = "All Time", periodFrom = "", periodTo = "", teacherId = "", teacherName = "All Teachers", pageIndex = 0) }
                load()
            }
            is ParentPortalNotesEvent.PageChanged -> { _state.update { it.copy(pageIndex = event.value.coerceAtLeast(0)) }; load() }
            is ParentPortalNotesEvent.PageSizeChanged -> { _state.update { it.copy(pageSize = event.value.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0) }; load() }
            is ParentPortalNotesEvent.SortChanged -> {
                _state.update { current -> current.copy(sortField = event.field, sortAscending = if (current.sortField == event.field) !current.sortAscending else true, pageIndex = 0) }
                load()
            }
            is ParentPortalNotesEvent.ViewRequested -> loadDetail(event.row)
            ParentPortalNotesEvent.ViewDismissed -> _state.update { it.copy(viewing = null, viewingDetail = null, viewingReplies = emptyList(), viewingConversationInfo = ParentConversationInfo(), isViewingLoading = false, viewingError = "", historyScrollGeneration = 0) }
            is ParentPortalNotesEvent.ReplySubmitted -> sendReply(event.message, event.attachmentFileIds)
        }
    }

    private fun load(debounce: Boolean = false) {
        listJob?.cancel()
        val requestId = ++listRequestId
        val initial = _state.value
        val childId = initial.selectedChild?.id?.trim()?.takeIf(String::isNotBlank)
        if (childId == null) {
            _state.update { it.copy(rows = emptyList(), matchingCount = 0, isLoading = false, errorMessage = "No linked child found.") }
            return
        }
        _state.update { it.copy(isLoading = true, errorMessage = "") }
        listJob = scope.launch {
            if (debounce) delay(300)
            val query = _state.value
            val child = query.selectedChild ?: return@launch
            val page = PageRequest(query.pageIndex, query.pageSize)
            val (createdFrom, createdTo) = query.toCreatedRange()
            ParentPortalNotesRepository.load(
                studentId = childId,
                status = query.status.toApiStatus(),
                limit = page.safePageSize,
                offset = page.offset,
                search = query.search,
                teacherId = query.teacherId,
                createdFrom = createdFrom,
                createdTo = createdTo,
                sortBy = query.sortField.apiValue,
                order = if (query.sortAscending) "asc" else "desc"
            ).onSuccess { raw ->
                if (requestId != listRequestId || _state.value.selectedChild?.id != childId) return@onSuccess
                val items = raw.firstArray("items", "parent_notes", "notes")
                val rows = items.map { it.jsonObject.toRow(child) }
                val totalCount = raw.firstInt("count", "total_count")
                if (totalCount == null) {
                    _state.update { it.copy(isLoading = false, errorMessage = "Parent notes response did not include the filtered count.") }
                    return@onSuccess
                }
                val lastPageIndex = (pageCountForTotal(totalCount, page.safePageSize) - 1).coerceAtLeast(0)
                if (page.safePageIndex > lastPageIndex) {
                    _state.update { it.copy(pageIndex = lastPageIndex) }
                    load()
                    return@onSuccess
                }
                val options = raw.firstArray("teacher_options").mapNotNull { option ->
                    val value = option.jsonObject
                    val id = value.firstString("id", "teacher_id").orEmpty()
                    val name = value.firstString("name", "teacher_name", "display_name").orEmpty()
                    if (id.isBlank() || name.isBlank()) null else ParentPortalTeacherOption(id, name)
                }
                _state.update { current ->
                    if (requestId != listRequestId) current else current.copy(
                        rows = rows,
                        matchingCount = totalCount,
                        teacherOptions = (options + rows.mapNotNull { row -> row.teacherId.takeIf(String::isNotBlank)?.let { ParentPortalTeacherOption(it, row.teacherName) } })
                            .distinctBy { it.id }.sortedBy { it.name.lowercase() },
                        isLoading = false,
                        errorMessage = ""
                    )
                }
            }.onFailure { error ->
                if (requestId == listRequestId) _state.update { it.copy(isLoading = false, errorMessage = error.message ?: "Unable to load parent notes.") }
            }
        }
    }

    private fun loadSummary(studentId: String) {
        summaryJob?.cancel()
        summaryJob = scope.launch {
            ParentPortalNotesRepository.loadSummary(studentId).onSuccess { raw ->
                if (_state.value.selectedChild?.id != studentId) return@onSuccess
                val source = raw.firstObject("parent_note_counters", "statistics", "summary", "counters") ?: raw
                _state.update { current -> current.copy(
                    totalConversations = source.firstInt("total", "total_conversations") ?: current.totalConversations,
                    unreadCount = source.firstInt("unread", "unread_count") ?: current.unreadCount,
                    awaitingReplyCount = source.firstInt("awaiting_reply", "awaiting_your_reply", "pending_parent") ?: current.awaitingReplyCount,
                    activeCount = source.firstInt("active", "active_conversations") ?: current.activeCount
                ) }
            }
        }
    }

    private fun loadDetail(row: ParentNoteRowUi, scrollToLatestOnSuccess: Boolean = false) {
        val studentId = _state.value.selectedChild?.id.orEmpty()
        _state.update { it.copy(viewing = row, viewingDetail = null, viewingReplies = emptyList(), isViewingLoading = true, viewingError = "") }
        scope.launch {
            ParentPortalNotesRepository.loadDetail(row.backendId.ifBlank { row.parentNoteId }, studentId).onSuccess { raw ->
                val detail = raw.toResponse(row)
                val replies = raw.toReplies(excludeNoteId = detail.id)
                val parent = ProfileRepository.loadParentProfile().getOrNull()
                val lastActivity = (listOfNotNull(detail.updatedAt, detail.createdAt) + replies.mapNotNull { it.createdAt }).maxOrNull().orEmpty()
                val conversationCode = detail.parentNoteId.orEmpty().toConversationCode()
                    .ifBlank { row.conversationCode.toConversationCode() }
                _state.update { it.copy(viewingDetail = detail, viewingReplies = replies, viewingConversationInfo = ParentConversationInfo(teacherName = row.teacherName, parentName = parent?.name.orEmpty(), mobile = parent?.mobile.orEmpty(), lastActivity = lastActivity, conversationId = conversationCode), isViewingLoading = false, historyScrollGeneration = if (scrollToLatestOnSuccess) it.historyScrollGeneration + 1 else it.historyScrollGeneration) }
            }.onFailure { error -> _state.update { it.copy(isViewingLoading = false, viewingError = error.message ?: "Unable to load conversation details.") } }
        }
    }

    private fun sendReply(message: String, attachmentFileIds: List<String>) {
        val row = _state.value.viewing ?: return
        val studentId = _state.value.selectedChild?.id ?: return
        if (message.trim().isBlank()) return
        _state.update { it.copy(isSendingReply = true) }
        scope.launch { ParentPortalNotesRepository.reply(row.backendId.ifBlank { row.parentNoteId }, message, attachmentFileIds).onSuccess { _state.update { it.copy(isSendingReply = false, replyResetGeneration = it.replyResetGeneration + 1) }; loadDetail(row, scrollToLatestOnSuccess = true); load(); loadSummary(studentId) }.onFailure { error -> _state.update { it.copy(isSendingReply = false, viewingError = error.message ?: "Unable to send reply.") } } }
    }

    fun clear() { listJob?.cancel(); summaryJob?.cancel(); scope.cancel() }
}

private fun String.toApiStatus(): String? = when (trim()) {
    "All" -> null
    "Active" -> "ACTIVE"
    "Awaiting Your Reply" -> "PENDING_PARENT"
    "Pending Teacher" -> "PENDING_TEACHER"
    "Unread" -> "UNREAD"
    "Closed" -> "CLOSED"
    else -> null
}

private fun ParentPortalNotesState.toCreatedRange(): Pair<String?, String?> {
    if (period == "Custom Range") return appDateInputToApi(periodFrom) to appDateInputToApi(periodTo)
    val today = Clock.System.now().toLocalDateTime(TimeZone.UTC).date
    return when (period) {
        "Today" -> today.toString() to today.toString()
        "Last 7 Days" -> today.minus(6, DateTimeUnit.DAY).toString() to today.toString()
        "This Month" -> LocalDate(today.year, today.month, 1).toString() to today.toString()
        else -> null to null
    }
}

private fun JsonObject.toRow(child: ParentAttendanceChild): ParentNoteRowUi {
    val id = (firstString("id", "note_id")
        ?: firstString("parent_note_id")?.takeIf { it.isUuid() }
        ).orEmpty()
    val parentNoteId = firstString("parent_note_id", "note_id", "id").orEmpty()
    return ParentNoteRowUi(
        backendId = id,
        parentNoteId = parentNoteId.ifBlank { "—" },
        studentName = firstString("student_name") ?: child.name,
        className = firstString("class_name", "class") ?: child.classLabel.substringBefore('-').ifBlank { "—" },
        section = firstString("section_name", "section") ?: child.classLabel.substringAfter('-', "—"),
        title = firstString("title", "subject") ?: "Untitled conversation",
        date = (firstString("last_activity", "last_activity_at", "updated_at", "created_at") ?: "").toDisplayTimestamp(),
        status = (firstString("status") ?: "Active").toDisplayStatus(),
        detail = firstString("message", "description", "content") ?: "",
        attachmentFileIds = firstStringList("attachment_file_ids", "file_ids") ?: emptyList(),
        teacherName = firstString("teacher_name", "teacher", "created_by_name") ?: "—",
        teacherId = firstString("teacher_id", "created_by_id").orEmpty(),
        conversationCode = firstString("parent_note_id")?.toConversationCode().orEmpty()
    )
}

private fun JsonObject.toResponse(row: ParentNoteRowUi): ParentNoteResponse {
    val noteId = firstString("id", "parent_note_id", "note_id") ?: row.backendId
    val originalMessage = firstArray("history", "conversation_history", "messages")
        .firstOrNull { item -> item.jsonObject.firstString("id") == noteId }
        ?.jsonObject
    return ParentNoteResponse(
        schoolId = firstString("school_id"),
        studentId = firstString("student_id"),
        parentId = firstString("parent_id"),
        parentNoteId = firstString("parent_note_id", "note_id", "id") ?: row.parentNoteId,
        title = firstString("title", "subject") ?: row.title,
        message = firstString("message", "description", "content") ?: row.detail,
        isActive = firstBoolean("is_active"),
        id = noteId,
        studentName = firstString("student_name") ?: row.studentName,
        className = firstString("class_name", "class") ?: row.className,
        sectionName = firstString("section_name", "section") ?: row.section,
        status = firstString("status") ?: row.status,
        createdAt = firstString("created_at") ?: originalMessage?.firstString("created_at", "sent_at"),
        updatedAt = firstString("updated_at", "last_activity_at"),
        attachmentFileIds = firstStringList("attachment_file_ids", "file_ids"),
        attachmentFileUrls = firstStringList("attachment_file_urls", "file_urls")
    )
}

private fun JsonObject.toReplies(excludeNoteId: String? = null): List<ParentNoteReplyResponse> = firstArray("replies", "history", "conversation_history", "messages")
    .map { value ->
        value.jsonObject.let { item ->
            ParentNoteReplyResponse(
                message = item.firstString("message", "content", "body"),
                id = item.firstString("id"),
                parentNoteId = item.firstString("parent_note_id"),
                senderId = item.firstString("sender_id", "created_by_id"),
                senderType = item.firstString("sender_type", "role"),
                senderName = item.firstString("sender_name", "author_name", "created_by", "created_by_name"),
                createdAt = item.firstString("created_at", "sent_at"),
                attachmentFileIds = item.firstStringList("attachment_file_ids", "file_ids"),
                attachmentFileUrls = item.firstStringList("attachment_file_urls", "file_urls")
            )
        }
    }
    .filterNot { reply -> !excludeNoteId.isNullOrBlank() && reply.id == excludeNoteId }
private fun JsonObject.firstString(vararg keys: String): String? = keys.firstNotNullOfOrNull { get(it)?.jsonPrimitive?.contentOrNull?.takeIf(String::isNotBlank) }
private fun JsonObject.firstBoolean(key: String): Boolean? = get(key)?.jsonPrimitive?.booleanOrNull
private fun JsonObject.firstInt(vararg keys: String): Int? = firstString(*keys)?.toIntOrNull()
private fun JsonObject.firstObject(vararg keys: String): JsonObject? = keys.firstNotNullOfOrNull { get(it)?.jsonObject }
private fun JsonObject.firstArray(vararg keys: String): JsonArray = keys.firstNotNullOfOrNull { get(it)?.jsonArray } ?: JsonArray(emptyList())
private fun JsonObject.firstStringList(vararg keys: String): List<String>? = keys.firstNotNullOfOrNull { get(it)?.jsonArray?.mapNotNull { value -> value.jsonPrimitive.contentOrNull?.takeIf(String::isNotBlank) } }
private fun String.toDisplayTimestamp(): String {
    val value = trim()
    if (value.isBlank()) return "—"
    val dateParts = value.substringBefore('T').split('-')
    if (dateParts.size != 3) return value
    val month = dateParts[1].toIntOrNull()?.let { listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec").getOrNull(it - 1) } ?: return value
    val date = "${dateParts[2].padStart(2, '0')} $month ${dateParts[0]}"
    val timeParts = value.substringAfter('T', "").substringBefore('+').substringBefore('Z').substringBefore('.').split(':')
    val hour = timeParts.getOrNull(0)?.toIntOrNull() ?: return date
    val minute = timeParts.getOrNull(1)?.toIntOrNull() ?: 0
    val suffix = if (hour >= 12) "PM" else "AM"
    val displayHour = (hour % 12).let { if (it == 0) 12 else it }
    return "$date, ${displayHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} $suffix"
}
private fun String.toDisplayStatus(): String = when (uppercase().replace('_', ' ')) { "PENDING PARENT", "AWAITING YOUR REPLY" -> "Awaiting Your Reply"; "PENDING TEACHER" -> "Pending Teacher"; "ACTIVE" -> "Active"; "UNREAD" -> "Unread"; "CLOSED" -> "Closed"; else -> replaceFirstChar { it.uppercase() } }
private fun String.isUuid(): Boolean = matches(Regex("[0-9a-fA-F]{8}-[0-9a-fA-F-]{27,}"))
private fun String.toConversationCode(): String = takeUnless(String::isUuid)?.trim().orEmpty()
